import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/BrandingContext";
import BrandLogo from "../components/BrandLogo";
import { formatApiError } from "../lib/api";

export default function Login() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const branding = useBranding();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const result = await login(email, password);
      if (result?.requires_verification) {
        toast.message(t("auth:login.verify_pending", "Verificá tu email — te enviamos un nuevo código"));
        navigate(`/verify-email?email=${encodeURIComponent(result.email)}`);
        return;
      }
      toast.success(t("auth:login.welcome_back", "¡Bienvenido de vuelta!"));
      if (result.role === "super_admin") navigate("/admin");
      else if (!result.onboarded) navigate("/onboarding");
      else navigate("/workspace");
    } catch (err) {
      const data = err.response?.data;
      if (err.response?.status === 403 && data?.requires_verification) {
        toast.message(t("auth:login.verify_pending", "Verificá tu email — te enviamos un nuevo código"));
        navigate(`/verify-email?email=${encodeURIComponent(data.email)}`);
        return;
      }
      toast.error(formatApiError(data?.detail) || t("auth:login.failed", "No pudimos iniciar tu sesión"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      title={t("auth:login.title", { app: branding.app_name || "Faro" })}
      subtitle={t("auth:login.subtitle")}
    >
      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <div>
          <Label htmlFor="email" className="text-xs uppercase tracking-wider font-mono">
            {t("auth:login.email")}
          </Label>
          <Input
            id="email"
            type="email"
            required
            autoFocus
            data-testid="login-email-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            className="mt-1.5 h-11 rounded-sm"
          />
        </div>
        <div>
          <Label htmlFor="password" className="text-xs uppercase tracking-wider font-mono">
            {t("auth:login.password")}
          </Label>
          <Input
            id="password"
            type="password"
            required
            data-testid="login-password-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className="mt-1.5 h-11 rounded-sm"
          />
        </div>
        <Button type="submit" disabled={busy} data-testid="login-submit-button" className="w-full h-11 rounded-sm">
          {busy ? t("auth:login.submitting", "Entrando…") : t("auth:login.submit")}
        </Button>
      </form>
      <div className="mt-6 text-sm text-muted-foreground">
        {t("auth:login.no_account")}{" "}
        <Link to="/signup" className="text-primary underline" data-testid="goto-signup">
          {t("auth:login.create_account")}
        </Link>
      </div>
    </AuthShell>
  );
}

export function AuthShell({ title, subtitle, children }) {
  const branding = useBranding();
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex border-r border-border bg-foreground text-background p-12 flex-col justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link-auth">
          <BrandLogo size="lg" />
        </Link>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-mono opacity-60">[ multi-model AI ]</div>
          <h2 className="mt-4 font-display font-black tracking-tight text-4xl lg:text-5xl leading-[1.05] max-w-md">
            {branding.tagline}
          </h2>
        </div>
        <div className="text-xs font-mono opacity-60">{branding.footer_text}</div>
      </div>
      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <h1 className="font-display font-extrabold tracking-tight text-3xl">{title}</h1>
          <p className="text-muted-foreground mt-1">{subtitle}</p>
          <div className="mt-8">{children}</div>
        </div>
      </div>
    </div>
  );
}
